import React, { useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import VizSensor from "react-visibility-sensor";
import ScrollContainer from "react-indiana-drag-scroll";
import windowWidthHook from "@hooks/window-width";
import { scroller } from "react-scroll";

import Tag from "@assets/icons/tag.inline.svg";
import Close from "@assets/icons/times-solid.inline.svg";
import Button from "@components/common/button/Button";
import { Cta } from "@components/common/cta/Cta";

import { ShopBillboardInterface } from "./ShopBillboard.interface";
import * as s from "./ShopBillboard.module.scss";

const ShopBillboard = ({ data }: ShopBillboardInterface) => {
  const { direction, title, blurb, images, products, cta } = data;
  const [showProducts, setShowProducts] = useState(false);
  const windowWidth = windowWidthHook();
  const isMobile = windowWidth < 769;
  const isTablet = windowWidth < 1023;
  const shouldScroll = isTablet ? true : false;
  const flipGrid = direction === "RTL" ? s.shopBillboardRTL : "";
  const [visibilityClass, setVisibilityClass] = useState(false);

  const onChangeVisibility = (isActive) => {
    if (isActive) {
      setVisibilityClass(true);
    }
  };

  const dataQuery = useStaticQuery(graphql`
    query allProducts {
      allProducts: allShopifyProduct {
        nodes {
          title
          description
          handle
          media {
            ... on ShopifyMediaImage {
              image {
                originalSrc
              }
            }
          }
          variants {
            price
          }
        }
      }
    }
  `);

  const taggedProducts = dataQuery.allProducts.nodes.filter((item) =>
    products.find((product) => item.handle === product.handle)
  );

  const backgroundImageUrl =
    isMobile && images[0].mobileImage
      ? images[0].mobileImage.sourceUrl
      : images[0].desktopImage.sourceUrl;

  const showProductsClass = showProducts
    ? s.shopBillboard__slideUp
    : s.shopBillboard__slideDown;

  const reverseProductsClass = showProducts
    ? s.shopBillboard__slideDown
    : s.shopBillboard__slideUp;

  return (
    <VizSensor partialVisibility onChange={(e) => onChangeVisibility(e)}>
      <div
        className={`${s.shopBillboard} ${flipGrid} ${
          visibilityClass && s.shopBillboard___isVisible
        } ${showProducts && flipGrid ? s.shopBillboard__activeRTL : ""}
        ${showProducts && flipGrid ? s.shopBillboard__active : ""}`}
      >
        <div className={`${s.shopBillboard__container}`}>
          <div className={`${s.shopBillboard__textArea}`}>
            <div
              className={`${s.shopBillboard__textAreaInner} ${showProductsClass}`}
            >
              <p className={s.shopBillboard__textAreaSubTitle}>Shop</p>
              <h3 className={s.shopBillboard__textAreaTitle}>{title}</h3>
              <p className={s.shopBillboard__textAreaBlurb}>{blurb}</p>
              <Cta
                title={cta[0].title}
                onClick={() => {
                  if (isTablet && !showProducts) {
                    scroller.scrollTo("products", {
                      smooth: true,
                      offset: -200,
                    });
                  }

                  setShowProducts(!showProducts);
                }}
              ></Cta>
            </div>
          </div>
          <ScrollContainer
            hideScrollbars={shouldScroll}
            className={`${s.shopBillboard__products} ${reverseProductsClass}`}
          >
            {taggedProducts.length > 0 ? (
              taggedProducts.map((product, i) => (
                <div key={i} className={s.shopBillboard__products___card}>
                  <div className={s.shopBillboard__products___cardInner}>
                    <img
                      className={s.shopBillboard__productsImage}
                      src={product.media[0].image.originalSrc}
                      alt={product.title}
                      height="250px"
                      width="auto"
                    />
                    <div>
                      <p className={s.shopBillboard__productsPrice}>
                        From £{product.variants[0].price}
                      </p>
                      <h4 className={s.shopBillboard__productsTitle}>
                        {product.title}
                      </h4>
                    </div>
                    <Button
                      to={`/shop/${product.handle}`}
                      className={s.shopBillboard__textArea___button}
                    >
                      Buy Now
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className={s.shopBillboard__products}>
                <div className={s.shopBillboard__textAreaOOS}>
                  <h3>Something caught your eye?</h3>
                  <p>We've got you covered!</p>
                  <Button
                    to="/shop"
                    className={s.shopBillboard__textArea___button}
                  >
                    Visit the shop
                  </Button>
                </div>
              </div>
            )}
          </ScrollContainer>
        </div>

        <div
          className={s.shopBillboard__imageArea}
          onClick={() => {
            if (isTablet && !showProducts) {
              scroller.scrollTo("products", {
                smooth: true,
                offset: -200,
              });
            }
            setShowProducts(!showProducts);
          }}
          style={{
            backgroundImage: `url(${backgroundImageUrl})`,
          }}
        >
          {showProducts && (
            <button
              className={s.shopBillboard__imageArea__closeButton}
              onClick={() => setShowProducts(false)}
            >
              <Close />
            </button>
          )}
          {taggedProducts.length > 0 && (
            <div className={s.shopBillboard__imageArea___taggedProducts}>
              <div>
                <Tag />
              </div>
              &nbsp;<h5>{products.length} Products Tagged</h5>
            </div>
          )}
        </div>
      </div>
    </VizSensor>
  );
};

export default ShopBillboard;
